/**
 * Processes an image file from an html file input event.
 * Checks if the file is an image file, then checks if the file size exceeds our predetermined size limit.
 * If it does we resize the file to meet our size limit and call `onSuccess` with the new resized file.
 * @param {File} file file from an html file input element
 * @param {{onSuccess: cb, onError: cb }} onSuccess if the file is read and processed correctly this callback will be invoked
 *                                        onError if there is something wrong with the file this callback is invoked with an error message
 */
export function processFile(
	file: File,
	{
		onSuccess,
		onError,
	}: { onSuccess: (file: Blob) => void; onError: (errorMsg: string) => void }
): void {
	if (!validImageFile(file)) {
		onError('Not an image')
		return
	}
	if (exceedsMaxSize(file)) {
		resizeImage(file)
			.then((file: Blob) => {
				onSuccess(file)
			})
			.catch((e: Error) => {
				console.error(e)
				onError('Failed to compress image')
			})
	} else {
		onSuccess(file)
	}
}

/**
 * Checks if file is an image file.
 * @param {File} file
 * @returns {boolean}
 */
const validImageFile = (file: File): boolean => {
	return Boolean(file.type.match(/image.*/))
}

/**
 * Checks to see if file size is too big
 * @param {File} file
 * @returns {boolean} file exceeds maximum size that server allows
 */
export const exceedsMaxSize = (file: File | Blob): boolean => {
	if (file.size > MAX_ALLOWED_FILE_SIZE) {
		return true
	} else {
		return false
	}
}
// Maximum allowed size of file in bytes: 2MB
const MAX_ALLOWED_FILE_SIZE = 2 * 1024 * 1024
// Max image width and height that we use to for resizing a large image
const MAX_SIZE = 1800

/**
 * Given an image file, resizes the image to fit within the MAX_SIZE defined while maintaining image aspect ratio.
 * Returns a promise of the resized image as a blob.
 * Most of the logic from this function is from the stack overflow post: https://stackoverflow.com/a/39235724
 * @param {File} file the file of the image needing to be resized.
 * @returns {Promise<Blob>} the resized file as a blob
 */
function resizeImage(file: File): Promise<Blob> {
	const reader = new FileReader()
	const image = new Image()
	const canvas = document.createElement('canvas')
	const dataURItoBlob = (dataURI: string) => {
		const bytes =
			// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			dataURI.split(',')[0].indexOf('base64') >= 0
				? // @ts-expect-error TS2769 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				  atob(dataURI.split(',')[1])
				: // @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				  unescape(dataURI.split(',')[1])
		// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		const mime = dataURI
			.split(',')[0]
			.split(':')[1]
			.split(';')[0]
		const max = bytes.length
		const ia = new Uint8Array(max)
		for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i)
		return new Blob([ia], {
			type: mime,
		})
	}
	const resize = () => {
		let width = image.width
		let height = image.height

		if (width > height) {
			if (width > MAX_SIZE) {
				height *= MAX_SIZE / width
				width = MAX_SIZE
			}
		} else {
			if (height > MAX_SIZE) {
				width *= MAX_SIZE / height
				height = MAX_SIZE
			}
		}
		canvas.width = width
		canvas.height = height
		canvas?.getContext('2d')?.drawImage(image, 0, 0, width, height)
		const dataUrl = canvas.toDataURL(file.type)
		return dataURItoBlob(dataUrl)
	}

	return new Promise((resolve: (file: Blob) => void) => {
		reader.onload = (readerEvent: any) => {
			image.onload = () => resolve(resize())
			image.src = readerEvent.target.result
		}
		reader.readAsDataURL(file)
	})
}
